import { Component, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ApplyPanelComponent, ButtonDirective, CheckboxComponent, ColorPickerComponent, NumberInputComponent } from '@imagise/shared';
import { TranslateModule } from '@ngx-translate/core';
import { Size2D } from '../../../domain/interfaces/shared';
import { MessagingService } from '../../../infrastructure/messaging/messaging.service';
import { RefreshEditOptionsMessage } from '../../../domain/models/messages';
import { EditorFormStore, EditorMergeLayout, isMeaningfulMergeDraft } from '../../../domain';
import { PanelLayoutComponent } from '../panel-layout/panel-layout.component';

const DEFAULT_BORDER_COLOR = '#000000';
const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TRANSPARENT_BACKGROUND = true;
const MAX_GRID_DIMENSION = 50;
const MAX_SPACING = 500;
const MAX_BORDER_WIDTH = 100;

interface MergeLayoutOption {
  layout: EditorMergeLayout;
  labelKey: string;
  icon: string;
}

@Component({
  selector: 'lib-merge-panel',
  host: {
    class: 'contents',
  },
  templateUrl: './merge-panel.component.html',
  imports: [
    ApplyPanelComponent,
    ButtonDirective,
    CheckboxComponent,
    ColorPickerComponent,
    MatIconModule,
    NumberInputComponent,
    PanelLayoutComponent,
    TranslateModule,
  ],
})
export class MergePanelComponent implements OnInit {
  private readonly messageBus = inject(MessagingService);
  private readonly editorStore = inject(EditorFormStore);

  constructor() {
    this.messageBus
      .of(RefreshEditOptionsMessage).pipe(takeUntilDestroyed()).subscribe(() => this.refresh());
  }

  readonly imageSize = input<Size2D | null>(null);

  readonly apply = output<void>();

  readonly cancelRequested = output<void>();

  readonly layoutOptions: MergeLayoutOption[] = [
    { layout: 'grid', labelKey: 'EDITOR.MERGE.LAYOUTS.GRID', icon: 'grid_view' },
    { layout: 'vertical', labelKey: 'EDITOR.MERGE.LAYOUTS.VERTICAL', icon: 'table_rows' },
    { layout: 'horizontal', labelKey: 'EDITOR.MERGE.LAYOUTS.HORIZONTAL', icon: 'view_column' },
    { layout: 'custom', labelKey: 'EDITOR.MERGE.LAYOUTS.CUSTOM', icon: 'open_with' },
  ];

  layout: EditorMergeLayout = 'grid';
  rowCount = 1;
  columnCount = 2;
  spacing = 0;
  borderWidth = 0;
  borderColor = DEFAULT_BORDER_COLOR;
  backgroundColor = DEFAULT_BACKGROUND_COLOR;
  useTransparentBackground = DEFAULT_TRANSPARENT_BACKGROUND;

  readonly maxGridDimension = MAX_GRID_DIMENSION;
  readonly maxSpacing = MAX_SPACING;
  readonly maxBorderWidth = MAX_BORDER_WIDTH;

  /** The backend merges two images minimum. */
  get canApply(): boolean {
    return isMeaningfulMergeDraft(this.editorStore.getDraft());
  }

  get isGridLayout(): boolean {
    return this.layout === 'grid';
  }

  get isCustomLayout(): boolean {
    return this.layout === 'custom';
  }

  ngOnInit(): void {
    this.refresh();
  }

  onLayoutSelected(layout: EditorMergeLayout): void {
    this.layout = layout;
    this.syncDraft();
  }

  onRowCountChanged(value: number): void {
    this.rowCount = this.normalizeGridDimension(value);
    this.syncDraft();
  }

  onColumnCountChanged(value: number): void {
    this.columnCount = this.normalizeGridDimension(value);
    this.syncDraft();
  }

  onSpacingChanged(value: number): void {
    this.spacing = Math.min(MAX_SPACING, Math.max(0, Math.round(value || 0)));
    this.syncDraft();
  }

  onBorderWidthChanged(value: number): void {
    this.borderWidth = Math.min(MAX_BORDER_WIDTH, Math.max(0, Math.round(value || 0)));
    this.syncDraft();
  }

  onBorderColorChanged(value: string): void {
    this.borderColor = value;
    this.syncDraft();
  }

  onTransparentBackgroundChanged(checked: boolean): void {
    this.useTransparentBackground = checked;
    this.syncDraft();
  }

  onBackgroundColorChanged(value: string): void {
    this.backgroundColor = value;
    this.useTransparentBackground = false;
    this.syncDraft();
  }

  onApply(): void {
    this.apply.emit();
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  private refresh(): void {
    const draft = this.editorStore.ensureMergeDraft();

    this.layout = draft?.params.layout ?? 'grid';
    this.rowCount = this.normalizeGridDimension(draft?.params.rowCount ?? 1);
    this.columnCount = this.normalizeGridDimension(draft?.params.columnCount ?? 2);
    this.spacing = Math.min(MAX_SPACING, Math.max(0, Math.round(draft?.params.spacing ?? 0)));
    this.borderWidth = Math.min(MAX_BORDER_WIDTH, Math.max(0, Math.round(draft?.params.borderWidth ?? 0)));
    this.borderColor = draft?.params.borderColor ?? DEFAULT_BORDER_COLOR;
    this.backgroundColor = draft?.params.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;
    this.useTransparentBackground = (draft?.params.backgroundColor ?? null) == null;
    this.syncDraft();
  }

  private syncDraft(): void {
    this.editorStore.updateMergeDraftSettings({
      layout: this.layout,
      rowCount: this.rowCount,
      columnCount: this.columnCount,
      spacing: this.spacing,
      borderWidth: this.borderWidth,
      borderColor: this.borderColor,
      backgroundColor: this.useTransparentBackground ? null : this.backgroundColor,
    });
  }

  private normalizeGridDimension(value: number): number {
    return Math.min(MAX_GRID_DIMENSION, Math.max(1, Math.round(value || 1)));
  }
}
